// ============================================================================
// SIGNALING CLIENT - WebSocket for WebRTC Signaling
// ============================================================================

import { EventEmitter } from './EventEmitter';

export interface SignalingMessage {
  type: string;
  roomCode?: string;
  peerId?: string;
  targetId?: string;
  payload?: unknown;
}

export interface SignalingEvents {
  connected: void;
  disconnected: void;
  roomCreated: { roomCode: string };
  roomJoined: { roomCode: string; peers: string[] };
  peerJoined: { peerId: string };
  peerLeft: { peerId: string };
  offer: { peerId: string; offer: RTCSessionDescriptionInit };
  answer: { peerId: string; answer: RTCSessionDescriptionInit };
  iceCandidate: { peerId: string; candidate: RTCIceCandidateInit };
  error: { message: string };
}

export class SignalingClient extends EventEmitter<SignalingEvents> {
  private ws: WebSocket | null = null;
  private serverUrl: string;
  private localPeerId: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor(serverUrl: string) {
    super();
    this.serverUrl = serverUrl;
    this.localPeerId = this.generatePeerId();
  }

  private generatePeerId(): string {
    return `peer_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 9)}`;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.serverUrl);

        this.ws.onopen = () => {
          console.log('[Signaling] Connected');
          this.reconnectAttempts = 0;
          this.emit('connected', undefined);
          resolve();
        };

        this.ws.onclose = () => {
          console.log('[Signaling] Disconnected');
          this.emit('disconnected', undefined);
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('[Signaling] Error:', error);
          reject(error);
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };
      } catch (e) {
        reject(e);
      }
    });
  }

  private handleMessage(data: string): void {
    try {
      const message: SignalingMessage = JSON.parse(data);

      switch (message.type) {
        case 'room-created':
          this.emit('roomCreated', { roomCode: message.roomCode! });
          break;

        case 'room-joined':
          this.emit('roomJoined', {
            roomCode: message.roomCode!,
            peers: message.payload as string[],
          });
          break;

        case 'peer-joined':
          this.emit('peerJoined', { peerId: message.peerId! });
          break;

        case 'peer-left':
          this.emit('peerLeft', { peerId: message.peerId! });
          break;

        case 'offer':
          this.emit('offer', {
            peerId: message.peerId!,
            offer: message.payload as RTCSessionDescriptionInit,
          });
          break;

        case 'answer':
          this.emit('answer', {
            peerId: message.peerId!,
            answer: message.payload as RTCSessionDescriptionInit,
          });
          break;

        case 'ice-candidate':
          this.emit('iceCandidate', {
            peerId: message.peerId!,
            candidate: message.payload as RTCIceCandidateInit,
          });
          break;

        case 'error':
          this.emit('error', { message: message.payload as string });
          break;

        default:
          console.warn('[Signaling] Unknown message type:', message.type);
      }
    } catch (e) {
      console.error('[Signaling] Failed to parse message:', e);
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[Signaling] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[Signaling] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch(() => {
        // Will trigger onclose -> attemptReconnect
      });
    }, delay);
  }

  send(message: SignalingMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[Signaling] Cannot send, not connected');
    }
  }

  // Room operations
  createRoom(): void {
    this.send({
      type: 'create-room',
      peerId: this.localPeerId,
    });
  }

  joinRoom(roomCode: string): void {
    this.send({
      type: 'join-room',
      roomCode,
      peerId: this.localPeerId,
    });
  }

  leaveRoom(): void {
    this.send({
      type: 'leave-room',
      peerId: this.localPeerId,
    });
  }

  // Signaling operations
  sendOffer(targetId: string, offer: RTCSessionDescriptionInit): void {
    this.send({
      type: 'offer',
      peerId: this.localPeerId,
      targetId,
      payload: offer,
    });
  }

  sendAnswer(targetId: string, answer: RTCSessionDescriptionInit): void {
    this.send({
      type: 'answer',
      peerId: this.localPeerId,
      targetId,
      payload: answer,
    });
  }

  sendIceCandidate(targetId: string, candidate: RTCIceCandidateInit): void {
    this.send({
      type: 'ice-candidate',
      peerId: this.localPeerId,
      targetId,
      payload: candidate,
    });
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
  }

  getLocalPeerId(): string {
    return this.localPeerId;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
